/*
 * Educational content only. No article may contain buy/sell instructions,
 * target/stop-loss levels, or performance claims.
 */
export const ARTICLES = [
  {
    slug: "what-is-open-interest",
    title: "What Is Open Interest?",
    category: "Derivatives Basics",
    readTime: "6 min read",
    summary:
      "Open interest measures the number of derivative contracts currently outstanding. Understanding how it is calculated — and what it cannot tell you — is fundamental to reading derivatives data.",
    sections: [
      {
        h: "Definition",
        p: [
          "Open interest (OI) is the total number of derivative contracts — futures or options — that are currently open and have not been squared off, exercised or expired. Each contract represents an obligation between a buyer and a seller, so one unit of open interest reflects one contract held on both sides.",
          "Unlike volume, which resets at the end of every session, open interest is a running total. Volume tells you how much trading happened; open interest tells you how much exposure is still outstanding.",
        ],
      },
      {
        h: "How it changes",
        p: [
          "Open interest rises when a new buyer and a new seller create a fresh contract. It falls when both sides of an existing contract close their positions. When a new participant takes over an existing position from someone exiting, open interest stays unchanged — only ownership shifts.",
        ],
        list: [
          "New buyer + new seller → open interest increases",
          "Existing buyer closes + existing seller closes → open interest decreases",
          "New participant replaces an exiting one → open interest unchanged",
        ],
      },
      {
        h: "How analysts use it",
        p: [
          "Because open interest describes outstanding exposure, changes in OI are often studied alongside price and volatility data to describe how positioning is evolving — for example whether exposure is concentrating at particular strikes or expiries.",
          "In quantitative research, OI is typically used as a descriptive feature: a measurement of market structure at a point in time, compared against its own history to judge whether current levels are ordinary or unusual.",
        ],
      },
      {
        h: "Important limitations",
        p: [
          "Open interest does not reveal who is positioned, why, or whether a position is speculative or a hedge. Two participants can hold identical exposure for entirely different reasons. OI also says nothing about future direction, and identical OI patterns have historically preceded very different outcomes.",
          "Data revisions, expiry-day effects and differences between exchange-reported datasets can all affect interpretation. Any analytical conclusion drawn from OI should be treated as one input among many, not as a forecast.",
        ],
      },
    ],
  },
  {
    slug: "how-options-greeks-work",
    title: "How Options Greeks Work",
    category: "Options Analytics",
    readTime: "8 min read",
    summary:
      "Delta, gamma, theta, vega and rho describe how an option's theoretical value responds to changes in market conditions. They are sensitivities — not predictions.",
    sections: [
      {
        h: "What the Greeks measure",
        p: [
          "Option pricing models express the theoretical value of an option as a function of several inputs: the underlying price, strike, time to expiry, volatility and interest rates. The Greeks are the partial derivatives of that value with respect to each input — in other words, sensitivities.",
        ],
        list: [
          "Delta — sensitivity of option value to a change in the underlying price",
          "Gamma — the rate at which delta itself changes",
          "Theta — sensitivity to the passage of time",
          "Vega — sensitivity to a change in implied volatility",
          "Rho — sensitivity to a change in interest rates",
        ],
      },
      {
        h: "Why gamma matters analytically",
        p: [
          "Gamma describes curvature. A position with high gamma sees its directional exposure change rapidly as the underlying moves, which means risk measured at one moment can be materially different a short time later. For this reason, risk analytics typically re-measure exposure continuously rather than relying on a single snapshot.",
        ],
      },
      {
        h: "Volatility and time",
        p: [
          "Vega and theta are often studied together because implied volatility and time decay both influence option premiums, sometimes in opposite directions. Analytically, this means the same price move in the underlying can produce quite different option outcomes depending on the volatility environment.",
        ],
      },
      {
        h: "Model dependence",
        p: [
          "Every Greek is model-dependent. Values computed under one set of assumptions — for example constant volatility — can differ from values computed under a more complex model. Greeks are therefore best understood as descriptive measurements under stated assumptions, and not as statements about what will happen.",
        ],
      },
    ],
  },
  {
    slug: "understanding-market-volatility",
    title: "Understanding Market Volatility",
    category: "Risk Analytics",
    readTime: "7 min read",
    summary:
      "Volatility quantifies the dispersion of returns. Distinguishing realised from implied volatility — and understanding volatility regimes — is central to quantitative risk analysis.",
    sections: [
      {
        h: "Realised vs implied volatility",
        p: [
          "Realised (or historical) volatility is computed from observed price returns over a chosen window. Implied volatility is extracted from traded option prices and reflects the volatility assumption embedded in those prices. They are different measurements and frequently diverge.",
          "Because realised volatility depends on the look-back window chosen, two analysts can report different numbers for the same instrument without either being wrong. Methodology disclosure matters.",
        ],
      },
      {
        h: "Volatility clustering",
        p: [
          "Financial return series commonly exhibit clustering: periods of elevated volatility tend to be followed by further elevated volatility, and calm periods by calm. This empirical property underlies volatility models such as the GARCH family, which model conditional variance over time.",
        ],
      },
      {
        h: "Term structure and skew",
        p: [
          "Implied volatility varies across expiries (term structure) and across strikes (skew). The shape of these surfaces is a descriptive feature of the options market at a point in time, and analysts often compare current shapes with historical distributions to judge whether conditions are typical.",
        ],
      },
      {
        h: "What volatility does not tell you",
        p: [
          "Volatility is a measure of dispersion, not of direction. High volatility does not indicate whether prices will rise or fall, and low volatility does not imply safety. Volatility measurements can also change abruptly, which is why risk analytics emphasise scenario ranges rather than single-point expectations.",
        ],
      },
    ],
  },
  {
    slug: "what-is-pcr",
    title: "What Is PCR (Put-Call Ratio)?",
    category: "Derivatives Basics",
    readTime: "5 min read",
    summary:
      "The put-call ratio is a simple ratio of put activity to call activity. Its usefulness depends entirely on how it is constructed and on the context in which it is read.",
    sections: [
      {
        h: "How it is calculated",
        p: [
          "The put-call ratio (PCR) divides put activity by call activity. It can be computed on volume (contracts traded in a session) or on open interest (contracts outstanding). The two versions can move in different directions and are not interchangeable.",
        ],
        list: [
          "PCR (volume) = put volume ÷ call volume",
          "PCR (open interest) = put open interest ÷ call open interest",
        ],
      },
      {
        h: "Reading it in context",
        p: [
          "A raw PCR figure carries little information on its own. Analysts typically compare it with its own recent distribution — for instance, whether the current value sits at the upper or lower end of the past year's range — because the 'normal' level varies by instrument, expiry cycle and market regime.",
        ],
      },
      {
        h: "Common pitfalls",
        p: [
          "PCR is distorted by hedging activity, expiry effects, illiquid strikes and index versus single-stock differences. A large institutional hedge can move the ratio without any change in sentiment. Because the ratio aggregates very different motivations into one number, it is a coarse descriptive statistic rather than a precise measure of positioning.",
        ],
      },
      {
        h: "Analytical use",
        p: [
          "In quantitative work, PCR is generally used as one feature among many, tested for statistical relationships rather than accepted as a standalone indicator. Historical studies of any single ratio should include base rates, sample sizes and out-of-sample checks before conclusions are drawn.",
        ],
      },
    ],
  },
  {
    slug: "how-machine-learning-analyses-market-data",
    title: "How Machine Learning Can Analyse Market Data",
    category: "AI & Machine Learning",
    readTime: "9 min read",
    summary:
      "Machine learning can classify, cluster and summarise large volumes of market and text data. Its usefulness depends on data quality, validation discipline and honest treatment of uncertainty.",
    sections: [
      {
        h: "Where ML fits",
        p: [
          "Machine learning is well suited to tasks with abundant data and a clearly defined objective: classifying documents, extracting entities from text, grouping instruments with similar statistical behaviour, or estimating a quantity such as conditional volatility.",
          "It is far less suited to tasks where the underlying relationships are unstable, the data is sparse, or the objective is ambiguous — conditions that occur frequently in financial markets.",
        ],
      },
      {
        h: "Feature engineering",
        p: [
          "Raw market data is rarely fed directly into models. It is transformed into features — normalised returns, volatility measures, liquidity statistics, market-structure descriptors, event flags. The quality of these features usually matters more than the choice of algorithm.",
        ],
      },
      {
        h: "Validation discipline",
        p: [
          "Financial data is ordered in time, so standard random cross-validation leaks information from the future into the past. Sound practice uses time-ordered splits, walk-forward evaluation, and explicit handling of transaction costs and survivorship effects.",
        ],
        list: [
          "Respect time ordering; never shuffle time-series data randomly",
          "Reserve genuinely unseen out-of-sample periods",
          "Report stability across periods, not just best-case results",
          "Account for data revisions, costs and liquidity constraints",
        ],
      },
      {
        h: "Limits and honesty",
        p: [
          "Overfitting is the central risk: with enough searching, a model can appear to explain historical data while capturing nothing durable. Models also degrade as market conditions change, which is why drift monitoring and periodic re-evaluation matter.",
          "AI-generated outputs can contain errors, omissions and delays. They are analytical aids for research and should not be treated as predictions of price or as a substitute for independent judgement or professional advice.",
        ],
      },
    ],
  },
  {
    slug: "how-quantitative-models-analyse-financial-markets",
    title: "How Quantitative Models Analyse Financial Markets",
    category: "Quantitative Research",
    readTime: "8 min read",
    summary:
      "Quantitative research applies statistical method to market questions: define a hypothesis, measure carefully, test honestly, and document what the result does and does not support.",
    sections: [
      {
        h: "The research process",
        p: [
          "Quantitative research is a process of disciplined measurement. It starts with a specific, testable question — not a conclusion in search of evidence — and proceeds through data assembly, feature definition, statistical testing and documentation.",
        ],
        list: [
          "State a hypothesis that could be shown to be false",
          "Assemble and validate the required data",
          "Define measurements precisely and reproducibly",
          "Test on data not used during development",
          "Document assumptions, limitations and negative results",
        ],
      },
      {
        h: "Statistical vs causal claims",
        p: [
          "Most findings in market data are statistical associations, not causal relationships. Confusing the two is a common source of error. A robust research write-up distinguishes what was measured from what was inferred, and states the strength of evidence.",
        ],
      },
      {
        h: "Backtesting carefully",
        p: [
          "A backtest is a simulation, and simulations inherit every assumption made about costs, liquidity, data timing and execution. Look-ahead bias, survivorship bias and repeated testing on the same data all inflate apparent quality. Back-tested or simulated results do not guarantee future outcomes.",
        ],
      },
      {
        h: "Risk framing",
        p: [
          "Quantitative analysis is often more informative about risk than about return. Distribution of outcomes, dispersion, drawdown statistics and sensitivity to assumptions are all measurable, and describing them clearly is usually more valuable than producing a single expected figure.",
        ],
      },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
